import * as XLSX from 'xlsx';
import { v4 as uuidv4 } from 'uuid';

/**
 * Parses Excel files for training data and updates asset objects
 * @param {File} file - The Excel file to parse
 * @param {Array} existingAssets - The existing assets to update
 * @returns {Promise<Array>} - Promise that resolves to an array of updated asset objects
 */
export function parseTrainingExcelFile(file, existingAssets) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        // Read the Excel file
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (!jsonData || jsonData.length === 0) {
          reject(new Error('Excel file is empty or invalid'));
          return;
        }
        
        // Validate required headers
        const requiredHeaders = ['assetId'];
        const firstRow = jsonData[0];
        const actualHeaders = Object.keys(firstRow);
        
        const missingHeaders = requiredHeaders.filter(header => 
          !actualHeaders.includes(header)
        );
        
        if (missingHeaders.length > 0) {
          reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
          return;
        }
        
        // Create a map of existing assets by assetId for quick lookup
        const assetMap = {};
        existingAssets.forEach(asset => {
          if (asset.assetId) {
            assetMap[asset.assetId] = asset;
          }
        });
        
        // Process training data rows
        const updatedAssets = [...existingAssets];
        const errors = [];
        let updatedCount = 0;
        
        jsonData.forEach((row, index) => {
          try {
            // Validate required fields
            if (!row.assetId) {
              errors.push(`Row ${index + 1}: Missing assetId`);
              return;
            }
            
            // Find the corresponding asset
            const asset = assetMap[row.assetId];
            if (!asset) {
              errors.push(`Row ${index + 1}: Asset with ID ${row.assetId} not found`);
              return;
            }
            
            // Update training fields if they exist in the row
            if (row.trainingDue !== undefined) {
              asset.trainingDue = String(row.trainingDue);
            }
            
            if (row.trainingDone !== undefined) {
              asset.trainingDone = String(row.trainingDone);
            }
            
            // Handle training not required flag
            if (row.trainingNotRequired !== undefined) {
              const notRequired = String(row.trainingNotRequired).toLowerCase();
              if (notRequired === 'yes' || notRequired === 'true' || notRequired === '1') {
                asset.trainingDue = 'NA';
                asset.trainingDone = 'NA';
              }
            }
            
            // Handle training type if provided
            if (row.trainingType !== undefined) {
              asset.trainingType = String(row.trainingType);
            }
            
            // Handle training provider if provided
            if (row.trainingProvider !== undefined) {
              asset.trainingProvider = String(row.trainingProvider);
            }
            
            // Handle training certificate number if provided
            if (row.certificateNumber !== undefined) {
              asset.certificateNumber = String(row.certificateNumber);
            }
            
            updatedCount++;
          } catch (rowError) {
            errors.push(`Row ${index + 1}: ${rowError.message}`);
          }
        });
        
        if (errors.length > 0) {
          reject(new Error(`Data validation errors:\n${errors.join('\n')}`));
          return;
        }
        
        console.log(`Successfully updated training data for ${updatedCount} assets`);
        resolve(updatedAssets);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Handles training file upload for Excel files
 * @param {File} file - The file to upload
 * @param {Array} existingAssets - The existing assets to update
 * @returns {Promise<Array>} - Promise that resolves to an array of updated asset objects
 */
export function handleTrainingFileUpload(file, existingAssets) {
  return new Promise((resolve, reject) => {
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      // Handle Excel files
      parseTrainingExcelFile(file, existingAssets)
        .then(resolve)
        .catch(reject);
    } else {
      reject(new Error('Unsupported file format. Please upload an Excel file (.xlsx or .xls).'));
    }
  });
}